import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter, Grid, List, Sparkles } from 'lucide-react';
import { Header } from '@/components/Header';
import { FilterSidebar } from '@/components/FilterSidebar';
import { ToolCard } from '@/components/ToolCard';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useTools, useLastSync, type Tool } from '@/hooks/useTools';
import { formatDistanceToNow } from 'date-fns';
import { AIFilter } from '@/components/AIFilter';
import { AIResults } from '@/components/AIResults';
import { smartSearchTools } from '@/lib/smartSearch';

interface FilterState {
  categories: string[];
  useCases: string[];
  pricing: string[];
  platforms: string[];
  aiEnabled: boolean | null;
}

const sortOptions = [
  { value: 'relevance', label: 'Relevance' },
  { value: 'name', label: 'Name A-Z' },
  { value: 'updated', label: 'Recently Updated' },
];

export default function Index() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'relevance');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showAIFilter, setShowAIFilter] = useState(false);
  const [aiResults, setAIResults] = useState<any>(null);
  
  const { data: tools = [], isLoading, error, refetch } = useTools();
  const { data: lastSync } = useLastSync();
  
  const [filters, setFilters] = useState<FilterState>({
    categories: searchParams.get('category')?.split(',').filter(Boolean) || [],
    useCases: searchParams.get('use_cases')?.split(',').filter(Boolean) || [],
    pricing: searchParams.get('pricing')?.split(',').filter(Boolean) || [],
    platforms: searchParams.get('platform')?.split(',').filter(Boolean) || [],
    aiEnabled: searchParams.get('ai') === '1' ? true : searchParams.get('ai') === '0' ? false : null,
  });

  // Sync URL params
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (searchQuery) params.set('q', searchQuery);
    if (sortBy !== 'relevance') params.set('sort', sortBy);
    if ((filters.categories || []).length) params.set('category', (filters.categories || []).join(','));
    if ((filters.useCases || []).length) params.set('use_cases', (filters.useCases || []).join(','));
    if ((filters.pricing || []).length) params.set('pricing', (filters.pricing || []).join(','));
    if ((filters.platforms || []).length) params.set('platform', (filters.platforms || []).join(','));
    if (filters.aiEnabled !== null) params.set('ai', filters.aiEnabled ? '1' : '0');

    setSearchParams(params, { replace: true });
  }, [searchQuery, sortBy, filters, setSearchParams]);

  // Filter and sort tools
  const filteredAndSortedTools = useMemo(() => {
    if (!tools.length) return [];
    
    // Apply smart search first if there's a query
    let filtered = searchQuery ? smartSearchTools(tools, searchQuery, 30) : tools;
    
    // Then apply other filters
    filtered = filtered.filter(tool => {

      // Category filter
      if ((filters.categories || []).length && !(filters.categories || []).some(cat => (tool.category || []).includes(cat))) {
        return false;
      }

      // Use cases filter
      if ((filters.useCases || []).length && !(filters.useCases || []).some(useCase => (tool.use_cases || []).includes(useCase))) {
        return false;
      }

      // Pricing filter
      if ((filters.pricing || []).length && !(filters.pricing || []).includes(tool.pricing)) {
        return false;
      }

      // Platform filter
      if ((filters.platforms || []).length && !(filters.platforms || []).some(platform => (tool.platforms || []).includes(platform))) {
        return false;
      }

      // AI filter
      if (filters.aiEnabled !== null && tool.ai_enabled !== filters.aiEnabled) {
        return false;
      }

      return true;
    });

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'updated':
          return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
        case 'relevance':
        default:
          // If there's a search query, maintain relevance order
          // Otherwise, fall back to name
          return searchQuery ? 0 : a.name.localeCompare(b.name);
      }
    });

    return filtered;
  }, [tools, searchQuery, filters, sortBy]);

  const activeFilterCount = 
    (filters.categories || []).length + 
    (filters.useCases || []).length + 
    (filters.pricing || []).length + 
    (filters.platforms || []).length + 
    (filters.aiEnabled !== null ? 1 : 0);

  const clearAllFilters = () => {
    setFilters({
      categories: [],
      useCases: [],
      pricing: [],
      platforms: [],
      aiEnabled: null,
    });
    setSearchQuery('');
  };

  const removeFilter = (type: keyof FilterState, value: string | boolean) => {
    if (type === 'aiEnabled') {
      setFilters(prev => ({ ...prev, aiEnabled: null }));
    } else {
      setFilters(prev => ({
        ...prev,
        [type]: (prev[type] as string[]).filter(item => item !== value)
      }));
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header 
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      />

      <div className="flex">
        <FilterSidebar
          filters={filters}
          onFiltersChange={setFilters}
          isOpen={filtersOpen}
          onClose={() => setFiltersOpen(false)}
        />

        <main className="flex-1 min-w-0">
          <div className="container mx-auto px-4 py-8">
            {/* Page title */}
            <div className="mb-8">
              <h1 className="text-3xl font-heading font-bold text-text-primary mb-2">
                Design Tools
              </h1>
              <p className="text-text-secondary text-lg">
                Discover, compare, and find the perfect UX/UI and product design tools for your workflow
              </p>
            </div>

            {/* AI Filter */}
            {!showAIFilter && !aiResults && (
              <div className="mb-6">
                <Button
                  onClick={() => setShowAIFilter(true)}
                  variant="outline"
                  className="border-accent/30 hover:border-accent hover:bg-accent-light/10 text-accent hover:text-accent hover:scale-105 transition-all duration-200"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  Try AI Filter
                  <Badge variant="secondary" className="ml-2 bg-accent-light text-accent text-xs">
                    Beta
                  </Badge>
                </Button>
              </div>
            )}

            {showAIFilter && !aiResults && (
              <AIFilter
                type="tools"
                data={tools} // Pass current tools data
                onResults={(results) => {
                  setAIResults(results);
                  setShowAIFilter(false);
                }}
                onClose={() => setShowAIFilter(false)}
              />
            )}

            {/* Remove the separate AI Results section */}
            {/* AI Results were moved to the main results section */}

            {/* Controls bar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div className="flex items-center space-x-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFiltersOpen(true)}
                  className="lg:hidden"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Filters
                  {activeFilterCount > 0 && (
                    <Badge variant="secondary" className="ml-2 bg-accent-light text-accent">
                      {activeFilterCount}
                    </Badge>
                  )}
                </Button>

                <div className="text-sm text-text-secondary">
                  {isLoading ? (
                    "Loading tools..."
                  ) : (
                    <>
                      <span className="font-medium text-text-primary">{filteredAndSortedTools.length}</span> tools found
                    </>
                  )}
                </div>

                {lastSync && (
                  <div className="text-xs text-text-tertiary">
                    Last updated {formatDistanceToNow(new Date(lastSync), { addSuffix: true })}
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-4">
                {/* View mode toggle */}
                <div className="flex items-center border border-border rounded-lg p-1">
                  <Button
                    variant={viewMode === 'grid' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('grid')}
                    className="h-8 px-3"
                  >
                    <Grid className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'list' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('list')}
                    className="h-8 px-3"
                  >
                    <List className="h-4 w-4" />
                  </Button>
                </div>

                {/* Sort */}
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sortOptions.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Active filters */}
            {(activeFilterCount > 0 || searchQuery) && (
              <div className="flex flex-wrap items-center gap-2 mb-6 p-4 bg-surface rounded-lg">
                <span className="text-sm text-text-secondary">Active filters:</span>
                
                {searchQuery && (
                  <Badge variant="secondary" className="bg-accent-light text-accent">
                    Search: "{searchQuery}"
                    <button
                      onClick={() => setSearchQuery('')}
                      className="ml-1 hover:text-error transition-colors duration-fast"
                    >
                      ×
                    </button>
                  </Badge>
                )}

                {(filters.categories || []).map(cat => (
                  <Badge key={cat} variant="secondary">
                    {cat}
                    <button
                      onClick={() => removeFilter('categories', cat)}
                      className="ml-1 hover:text-error transition-colors duration-fast"
                    >
                      ×
                    </button>
                  </Badge>
                ))}

                {(filters.pricing || []).map(price => (
                  <Badge key={price} variant="secondary">
                    {price}
                    <button
                      onClick={() => removeFilter('pricing', price)}
                      className="ml-1 hover:text-error transition-colors duration-fast"
                    >
                      ×
                    </button>
                  </Badge>
                ))}

                {(filters.platforms || []).map(platform => (
                  <Badge key={platform} variant="secondary">
                    {platform}
                    <button
                      onClick={() => removeFilter('platforms', platform)}
                      className="ml-1 hover:text-error transition-colors duration-fast"
                    >
                      ×
                    </button>
                  </Badge>
                ))}

                {filters.aiEnabled !== null && (
                  <Badge variant="secondary">
                    {filters.aiEnabled ? 'AI-powered' : 'Traditional tools'}
                    <button
                      onClick={() => removeFilter('aiEnabled', filters.aiEnabled!)}
                      className="ml-1 hover:text-error transition-colors duration-fast"
                    >
                      ×
                    </button>
                  </Badge>
                )}

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllFilters}
                  className="text-text-tertiary hover:text-error text-sm"
                >
                  Clear all
                </Button>
              </div>
            )}

            {/* Results */}
            {error ? (
              <div className="text-center py-16">
                <div className="max-w-md mx-auto">
                  <div className="h-24 w-24 mx-auto mb-6 rounded-full bg-surface flex items-center justify-center">
                    <Filter className="h-8 w-8 text-text-tertiary" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-text-primary mb-2">
                    Error loading tools
                  </h3>
                  <p className="text-text-secondary mb-6">
                    {error.message}
                  </p>
                  <Button onClick={() => refetch()} variant="outline">
                    Try again
                  </Button>
                </div>
              </div>
            ) : aiResults ? (
              /* Show only AI results when AI search is active */
              <AIResults
                results={aiResults?.results || []}
                appliedFilters={aiResults?.appliedFilters || {}}
                intent={aiResults?.intent || ''}
                totalFound={aiResults?.totalFound || 0}
                type="tools"
                onClose={() => setAIResults(null)}
              />
            ) : isLoading ? (
              <div className="text-center py-16">
                <div className="text-text-secondary">Loading tools...</div>
              </div>
            ) : filteredAndSortedTools.length > 0 ? (
              <div className={
                viewMode === 'grid' 
                  ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6"
                  : "space-y-4"
              }>
                {filteredAndSortedTools.map(tool => (
                  <ToolCard key={tool.id} tool={tool} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="max-w-md mx-auto">
                  <div className="h-24 w-24 mx-auto mb-6 rounded-full bg-surface flex items-center justify-center">
                    <Filter className="h-8 w-8 text-text-tertiary" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-text-primary mb-2">
                    No tools found
                  </h3>
                  <p className="text-text-secondary mb-6">
                    No matches for your current search and filters. Try clearing some filters or broadening your search.
                  </p>
                  <Button onClick={clearAllFilters} variant="outline">
                    Clear all filters
                  </Button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
